import { useState, useEffect } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './components/ThemeProvider';
import { LocalizationProvider, useLocalization } from './contexts/LocalizationContext';
import { StoreSettingsProvider, useStoreSettings } from './contexts/StoreSettingsContext';
import { ColorProvider } from './contexts/ColorContext';
import { AppRoutes } from './routes';
import { routes, publicRoutes } from './config';
import { clearAllRecaptchaVerifiers } from './lib/firebase';
import type { RegisterData } from './types';
import type { ConfirmationResult } from 'firebase/auth';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { queryClient } from './lib/queryClient';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, firebaseUser, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { storeSettings, loading: storeSettingsLoading } = useStoreSettings();
  const { t } = useLocalization();
  const [confirmationResult] = useState<ConfirmationResult | null>(null);
  const [registerFormData] = useState<RegisterData>({
    firstName: '',
    lastName: '',
    dateOfBirth: { year: '', month: '', day: '' },
    gender: '',
    referralCode: '',
  });
  const [hasHandledInitialRoute, setHasHandledInitialRoute] = useState(false);

  // Cleanup reCAPTCHA on component unmount
  useEffect(() => {
    return () => {
      clearAllRecaptchaVerifiers();
    };
  }, []);

  // Handle authentication state changes - only on first load
  useEffect(() => {
    // Don't redirect while authentication state is still loading
    if (isAuthLoading) {
      return;
    }

    // Only handle initial route once
    if (hasHandledInitialRoute) {
      return;
    }

    // Normalize path by removing trailing slash
    const currentPath = location.pathname.replace(/\/$/, '');
    const isPublic = publicRoutes.includes(currentPath as any);

    console.log('App.tsx: Handling initial route:', currentPath);
    console.log('App.tsx: isPublic:', isPublic);
    console.log('App.tsx: isAuthenticated:', isAuthenticated);
    console.log('App.tsx: customer:', customer);
    console.log('App.tsx: firebaseUser:', firebaseUser);

    // Skip handling for specific routes that should handle their own navigation
    if (currentPath === routes.registerSuccess ||
      currentPath === routes.otp ||
      currentPath === routes.register) {
      console.log('App.tsx: Skipping route handling for:', currentPath);
      setHasHandledInitialRoute(true);
      return;
    }

    // Check if user is fully authenticated (has both Firebase auth and customer data)
    const isFullyAuthenticated = isAuthenticated && customer && firebaseUser;

    // Only redirect if we're not already on the correct page
    if (isFullyAuthenticated) {
      // User is fully authenticated - redirect to home if on public routes
      if (isPublic && currentPath !== routes.home) {
        console.log('App.tsx: User fully authenticated, redirecting to home');
        navigate(routes.home, { replace: true });
      } else {
        // User fully authenticated and on protected route - allowing access
        console.log('App.tsx: User fully authenticated, allowing access to:', currentPath);
      }
    } else {
      // User is not authenticated - redirect to login if on protected routes
      if (!isPublic && currentPath !== routes.login) {
        console.log('App.tsx: User not authenticated, redirecting to login');
        navigate(routes.login, { replace: true });
      } else {
        // User not authenticated but on public route - allowing access
        console.log('App.tsx: User not authenticated, allowing access to public route:', currentPath);
      }
    }

    // Mark as handled
    setHasHandledInitialRoute(true);
  }, [isAuthenticated, isAuthLoading, location.pathname, navigate, customer, firebaseUser, hasHandledInitialRoute]);

  if (storeSettingsLoading){
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show loading screen while authentication state or store settings are being loaded
  if (isAuthLoading) {
    // Check if we're heading to the home page
    const currentPath = location.pathname.replace(/\/$/, '');
    const isGoingToHome = currentPath === routes.home || currentPath === '' || currentPath === '/';

    // For home route, show a skeleton that matches the home page layout
    if (isGoingToHome) {
      const skeletonContent = (
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: '#F8F9FA',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header Skeleton */}
          <Box
            sx={{
              backgroundColor: 'white',
              px: 2,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ width: '40px', height: '40px', borderRadius: '4px', bgcolor: '#E0E0E0' }} />
            <Box sx={{ width: '40px', height: '40px', borderRadius: '4px', bgcolor: '#E0E0E0' }} />
          </Box>

          {/* Main Content Skeleton */}
          <Box sx={{ flex: 1, p: 2, mt: -2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Membership Card Skeleton */}
            <Box
              sx={{
                width: 'calc(100% + 32px)',
                marginLeft: '-16px',
                backgroundColor: '#FFFFFF',
                borderBottomLeftRadius: '48px',
                borderBottomRightRadius: '48px',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.08)',
                p: 2,
              }}
            >
              <Box
                sx={{
                  width: 'calc(100% - 36px)',
                  height: '180px',
                  mx: 'auto',
                  borderRadius: '12px',
                  bgcolor: '#E0E0E0',
                  mb: 2,
                }}
              />
              {/* Progress bar skeleton */}
              <Box sx={{ width: 'calc(100% - 36px)', mx: 'auto', mb: 2 }}>
                <Box sx={{ height: '4px', borderRadius: '2px', bgcolor: '#F0F0F0', mb: 1.5 }} />
                <Box sx={{ height: '16px', borderRadius: '4px', bgcolor: '#F0F0F0', width: '70%' }} />
              </Box>
            </Box>

            {/* Refer a Friend Skeleton */}
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                px: 3,
                py: 2.5,
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ height: '20px', width: '120px', borderRadius: '4px', bgcolor: '#E0E0E0' }} />
                <Box sx={{ height: '20px', width: '80px', borderRadius: '4px', bgcolor: '#E0E0E0' }} />
              </Box>
            </Box>

            {/* Tabs Skeleton */}
            <Box>
              <Box sx={{ display: 'flex', gap: 2, borderBottom: '1px solid #D9D9D9', pb: 1, mb: 2 }}>
                <Box sx={{ height: '20px', width: '100px', borderRadius: '4px', bgcolor: '#E0E0E0' }} />
                <Box sx={{ height: '20px', width: '100px', borderRadius: '4px', bgcolor: '#F0F0F0' }} />
              </Box>
              {/* Coupon List Skeleton */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      height: '120px',
                      borderRadius: '8px',
                      bgcolor: '#F0F0F0',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      );

      // Check if desktop (match the same breakpoint as Home page)
      const isDesktop = window.innerWidth >= 900; // md breakpoint

      if (isDesktop) {
        return (
          <Box
            sx={{
              minHeight: '100vh',
              width: '100%',
              position: 'relative',
              background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
              backgroundSize: 'cover',
              backgroundAttachment: 'fixed',
              backgroundPosition: 'top',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: '60%',
                maxWidth: '420px',
                backgroundColor: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                mt: 2,
                mb: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              }}
            >
              {skeletonContent}
            </Box>
          </Box>
        );
      }

      return skeletonContent;
    } else {
      // Show full-screen loading for non-home routes
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <img src={storeSettings?.logoUrl || '/icons/logo.svg'} alt="Store Logo" style={{ height: '50px', width: 'auto' }} />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              fontSize: '32px',
            }}
          >
            {storeSettings?.text || 'GRINDIE'}
          </Typography>
          <CircularProgress
            size={40}
            sx={{
              color: 'white',
              mb: 2,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: 'white',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              opacity: 0.9,
            }}
          >
            {t("common.loading")}
          </Typography>
        </Box>
      );
    }
  }

  const handleBack = () => {
    if (location.pathname === routes.referralCode) {
      navigate(routes.register);
    } else {
      navigate(-1);
    }
  };

  const handleSwitchToRegister = () => {
    navigate(routes.register);
  };

  const handleBackToLogin = () => {
    navigate(routes.login);
  };

  return (

    <AppRoutes
      currentUser={customer}
      confirmationResult={confirmationResult}
      registerFormData={registerFormData}
      firebaseUser={firebaseUser}
      onBack={handleBack}
      onSwitchToRegister={handleSwitchToRegister}
      onBackToLogin={handleBackToLogin}
    />

  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <StoreSettingsProvider>
          <ColorProviderWrapper>
            <ThemeProvider>
              <LocalizationProvider>
                <BrowserRouter>
                  <AppContent />
                </BrowserRouter>
              </LocalizationProvider>
            </ThemeProvider>
          </ColorProviderWrapper>
        </StoreSettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Wrapper component to access storeSettings context
function ColorProviderWrapper({ children }: { children: React.ReactNode }) {
  const { storeSettings } = useStoreSettings();
  
  return (
    <ColorProvider colorPalettes={storeSettings?.colorPalettes}>
      {children}
    </ColorProvider>
  );
} 
